import { useState, useEffect, createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../services/api";
import { globalErrorHandler, ErrorUtils } from "../lib/errorHandler";
import { toast } from "sonner";
import Cookies from "js-cookie";
import { authServiceEnhanced } from "../services/api";
import { EnhancedToast } from "../components/ui/enhanced-toast";

// =====================================
// CONTEXTE D'AUTHENTIFICATION ENHANCED
// =====================================

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialiser l'authentification au montage
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      setLoading(true);

      if (authServiceEnhanced.isAuthenticated()) {
        const userData = authServiceEnhanced.getUser();
        // Utiliser les données locales au lieu de faire un appel API
        // pour accélérer l'initialisation et éviter le flash
        setUser(userData);
        setIsAuthenticated(true);

        // Optionnel : refresh des données utilisateur en arrière-plan
        // authServiceEnhanced.getCurrentUser().then(currentUser => {
        //   if (currentUser) setUser(currentUser);
        // }).catch(() => {});
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error("Erreur d'initialisation auth:", error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, rememberMe = false) => {
    try {
      setLoading(true);

      const response = await authServiceEnhanced.login(username, password, rememberMe);

      setUser(response.user);
      setIsAuthenticated(true);

      return {
        success: true,
        user: response.user,
        message: t("auth.loginSuccess"),
      };
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);

      // L'erreur est déjà affichée par le service enhanced
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authServiceEnhanced.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const hasRole = (role) => {
    return authServiceEnhanced.hasRole(role);
  };

  const hasPermission = (permission) => {
    return authServiceEnhanced.hasPermission(permission);
  };

  const requireAuth = () => {
    if (!isAuthenticated) {
      EnhancedToast.error(t("auth.accessUnauthorized"), {
        description: t("auth.mustBeLoggedIn"),
        action: {
          label: t("auth.signIn"),
          onClick: () => (window.location.href = "/login"),
        },
      });
      return false;
    }
    return true;
  };

  const requireRole = (role) => {
    if (!requireAuth()) return false;

    if (!hasRole(role)) {
      EnhancedToast.error(t("auth.accessDenied"), {
        description: t("auth.roleRequired", { role }),
        action: {
          label: t("auth.contactAdmin"),
          onClick: () => {},
        },
      });
      return false;
    }
    return true;
  };

  const requirePermission = (permission) => {
    if (!requireAuth()) return false;

    if (!hasPermission(permission)) {
      EnhancedToast.error(t("auth.permInsufficient"), {
        description: t("auth.permRequired", { permission }),
        action: {
          label: t("auth.viewPermissions"),
          onClick: () => {},
        },
      });
      return false;
    }
    return true;
  };

  const contextValue = {
    // État
    user,
    loading,
    isLoading: loading,  // Alias pour compatibilité
    isAuthenticated,

    // Actions
    login: login,
    logout: logout,

    // Vérifications
    hasRole,
    hasPermission,
    requireAuth,
    requireRole,
    requirePermission,

    // Utilitaires
    refresh: initializeAuth,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
